/*
  The SME's walk along a hand-over (DEC-1016-52, ui-direction screen 4, UI-04). The order is made once, on the server: an
  element that others in the hand-over wait for (their home) comes before them, then the Unsure ones, then the order of the
  item. The inbox carries it as `InboxHandOver.walk` (the ids of the elements still waiting) and a card carries its place in
  it as `SmeCardView.walk` ("Card n of m") with `prev_element_id` and `next_element_id`, which stay inside the hand-over.

  The screen only reads that order. "Open the first card", the beads, "Card n of m" and the stepper all come from it, so they
  cannot disagree. The one thing kept here is a last resort: a hand-over whose walk is empty although its cards are in the
  inbox (a server that has not filled it) opens on the first of its cards in the order the inbox lists them.
*/

import type { Inbox, InboxEntry, InboxHandOver } from "../../../api/types";

/** Every entry of the inbox, in the order the groups are shown. */
export function allEntries(inbox: Inbox): InboxEntry[] {
  return inbox.groups.flatMap((group) => group.entries);
}

/** The entries of one hand-over in the server's walk order. Ids the inbox does not list any more are left out. */
export function handOverRun(inbox: Inbox, handOverId: string): InboxEntry[] {
  const entries = allEntries(inbox);
  const walk = inbox.hand_overs.find((handOver) => handOver.id === handOverId)?.walk ?? [];
  if (walk.length > 0) {
    const byId = new Map(entries.map((entry) => [entry.element.id, entry]));
    return walk.flatMap((id) => {
      const entry = byId.get(id);
      return entry ? [entry] : [];
    });
  }
  return entries.filter((entry) => entry.hand_over_id === handOverId);
}

/** The element to open first in a hand-over, or null when none of its cards waits any more. */
export function firstCardOf(inbox: Inbox, handOver: InboxHandOver): string | null {
  return handOver.walk?.[0] ?? handOverRun(inbox, handOver.id)[0]?.element.id ?? null;
}
